import Decimal from 'decimal.js'
import moment from 'moment'
import { calculateSelectionsExtra } from './cartCalculations'
import { logger } from './logger'
import { PROMO_TYPES } from '@common'

const getMinutes = timeStr => {
    if (!timeStr) return null
    const [hours, minutes] = timeStr.split(':').map(Number)
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null
    return hours * 60 + minutes
}

const isWithinTimeWindows = (now, timeWindows) => {
    if (!Array.isArray(timeWindows) || timeWindows.length === 0) return true
    const nowMinutes = now.hours() * 60 + now.minutes()

    return timeWindows.some(window => {
        const start = getMinutes(window?.start)
        const end = getMinutes(window?.end)
        if (start === null || end === null) return false
        if (start === end) return true
        if (start < end) {
            return nowMinutes >= start && nowMinutes <= end
        }
        // Crosses midnight
        return nowMinutes >= start || nowMinutes <= end
    })
}

const isPromoActive = (promo, referenceMoment) => {
    if (!promo?.enabled) {
        logger.debug('[PromoEligibility] Promo disabled', { promoType: promo?.promoType })
        return false
    }
    const now = moment(referenceMoment)
    const weekdays = promo.weekdays
    if (Array.isArray(weekdays) && weekdays.length > 0) {
        const dayIndex = now.isoWeekday()
        if (!weekdays.includes(dayIndex)) {
            logger.debug('[PromoEligibility] Weekday not eligible', {
                promoType: promo.promoType,
                weekday: dayIndex,
                allowedWeekdays: weekdays,
            })
            return false
        }
    }
    const withinTimeWindow = isWithinTimeWindows(now, promo.timeWindows)
    if (!withinTimeWindow) {
        logger.debug('[PromoEligibility] Time window not eligible', {
            promoType: promo.promoType,
            timeWindows: promo.timeWindows,
            now: now.format('HH:mm'),
        })
    }
    return withinTimeWindow
}

const normalizeTagValue = tag => {
    if (tag === null || tag === undefined) return null
    if (typeof tag === 'object') {
        const value = tag.id ?? tag.tagId ?? tag.name ?? tag.label
        if (value === null || value === undefined) return null
        return String(value).toLowerCase()
    }
    return String(tag).toLowerCase()
}

const normalizeTagList = tags => {
    if (!Array.isArray(tags)) return []
    return tags.map(normalizeTagValue).filter(Boolean)
}

const buildCartCups = items => {
    const cups = []

    if (!Array.isArray(items)) return cups

    items.forEach(item => {
        const selectionExtra = calculateSelectionsExtra(item.selections)
        const variants = Array.isArray(item.variants) ? item.variants : []

        variants.forEach(variant => {
            const quantity = variant?.quantity || 0
            const unitPrice = (variant?.sellPrice || 0) + selectionExtra
            for (let i = 0; i < quantity; i += 1) {
                cups.push({
                    dishId: item.dishId,
                    cafeStallId: item.cafeStallId,
                    unitPrice,
                    tags: normalizeTagList(item.tags),
                })
            }
        })
    })

    return cups
}

const sumCupPrices = cups => cups.reduce((acc, cup) => acc.plus(cup?.unitPrice || 0), new Decimal(0))

export const getEligiblePromos = (items, availablePromos, referenceMoment = moment()) => {
    if (!Array.isArray(items) || items.length === 0) return []
    if (!Array.isArray(availablePromos) || availablePromos.length === 0) return []

    const cups = buildCartCups(items)
    const cartTotalDecimal = sumCupPrices(cups)
    const cartTotal = cartTotalDecimal.toNumber()
    const cartDishIds = Array.from(new Set(cups.map(cup => String(cup.dishId)))).sort()
    const cartTags = Array.from(new Set(cups.flatMap(cup => cup.tags))).sort()

    logger.debug('[PromoEligibility] Start evaluation', {
        cartTotal,
        cupCount: cups.length,
        cartDishIds,
        cartTags,
        promoCount: availablePromos.length,
        referenceTime: moment(referenceMoment).format(),
    })

    return availablePromos.reduce((acc, promo) => {
        if (!isPromoActive(promo, referenceMoment)) return acc
        const promoType = promo.promoType
        const eligibleDishIds = (Array.isArray(promo.eligibleDishIds) ? promo.eligibleDishIds : []).map(id => String(id))
        const eligibleTagIds = normalizeTagList(promo.eligibleTagIds)

        const hasDishFilter = eligibleDishIds.length > 0
        const hasTagFilter = eligibleTagIds.length > 0

        logger.debug('[PromoEligibility] Eligibility filters', {
            promoType,
            dishIdCount: eligibleDishIds.length,
            tagCount: eligibleTagIds.length,
        })

        if (!hasDishFilter && !hasTagFilter) {
            logger.debug('[PromoEligibility] Missing eligible dish ids/tags', { promoType })
            return acc
        }

        const matchesEligibilityFilter = cup => {
            if (!hasDishFilter && !hasTagFilter) {
                return true
            }

            const matchesDish = hasDishFilter && eligibleDishIds.includes(String(cup.dishId))
            const matchesTag = hasTagFilter && Array.isArray(cup.tags) && cup.tags.some(tag => eligibleTagIds.includes(tag))
            return matchesDish || matchesTag
        }

        if (promoType === PROMO_TYPES.MORNING_B3G1) {
            const requiresStallMatch = promo.stallId !== null && promo.stallId !== undefined
            const eligibleCups = cups.filter(
                cup => matchesEligibilityFilter(cup) && (!requiresStallMatch || Number(cup.cafeStallId) === Number(promo.stallId))
            )
            const freeCount = Math.floor(eligibleCups.length / 4)
            if (freeCount < 1) {
                logger.debug('[PromoEligibility] B3G1 insufficient quantity', {
                    promoType,
                    eligibleCount: eligibleCups.length,
                })
                return acc
            }
            const sortedByPrice = eligibleCups.map(cup => cup.unitPrice).sort((a, b) => a - b)
            const discountAmount = sortedByPrice.slice(0, freeCount).reduce((sum, price) => sum.plus(price), new Decimal(0))
            logger.debug('[PromoEligibility] B3G1 eligible', {
                promoType,
                eligibleCount: eligibleCups.length,
                freeCount,
                discountAmount: discountAmount.toNumber(),
            })
            acc.push({
                promoType,
                displayName: promo.displayName,
                discountAmount: discountAmount.toNumber(),
                freeCount,
            })
            return acc
        }

        if (promoType === PROMO_TYPES.KOPI_TEH_1OFF_MIN5) {
            const eligibleCups = cups.filter(cup => matchesEligibilityFilter(cup))
            if (eligibleCups.length === 0) {
                logger.debug('[PromoEligibility] Kopi/Teh no eligible items', { promoType })
                return acc
            }
            const lowest = new Decimal(Math.min(...eligibleCups.map(cup => cup.unitPrice)))
            const netTotal = cartTotalDecimal.minus(lowest)
            if (netTotal.lt(5)) {
                logger.debug('[PromoEligibility] Min spend not met', {
                    promoType,
                    cartTotal,
                    lowestEligible: lowest.toNumber(),
                    netTotal: netTotal.toNumber(),
                })
                return acc
            }
            logger.debug('[PromoEligibility] Kopi/Teh eligible', {
                promoType,
                eligibleCount: eligibleCups.length,
                cartTotal,
                lowestEligible: lowest.toNumber(),
                netTotal: netTotal.toNumber(),
            })
            acc.push({
                promoType,
                displayName: promo.displayName,
                discountAmount: 1,
            })
        }

        return acc
    }, [])
}
